import type {
  BrokerDateRange,
  BrokerListQuery,
  BrokerPeriod,
  BrokerReviewsQuery,
  BrokerStockSort,
  BrokerStocksQuery,
  BrokerTradesQuery,
  BusyNamesQuery,
  LargeHoldingsQuery,
  LargeTradeLeadersQuery,
  LargeTradesQuery,
  PVRangeQuery,
} from '@/schemas/broker'

/**
 * Query key factory for Broker Chirfaar.
 *
 * ONE BRANCH PER RESPONSE SHAPE, never one key parameterised by the tab that
 * happens to be open. The same rule as src/features/floorsheet/api/keys.ts and
 * for the same reason: a shared key lets React Query hand a component the
 * previous tab's rows for a frame, and here the shapes are far enough apart
 * that "previous rows" means a crash rather than a flicker — Compare's table
 * rows are brokers, its "Stocks in common" rows are symbols, and the large
 * trades leaderboard leans `buying`/`selling` where everything else leans
 * `bought`/`sold`.
 *
 * `broker(code)` is a scope rather than a query: it prefixes everything that
 * belongs to ONE broker's page, so the reviews mutation can invalidate exactly
 * that broker's reviews (`reviewsFor`) without touching the fifty-one other
 * houses, and a code change drops the whole page at once.
 *
 * Whole query objects go in the key rather than their fields spelled out.
 * React Query hashes them stably, and a filter added to a schema is then
 * automatically part of the key instead of silently serving a stale page —
 * which is how the movers pager once paged without refetching.
 */
export const brokerKeys = {
  all: ['brokers'] as const,

  // ── Surfaces that stand on their own ──────────────────────────────────────

  /** Overview: market tiles + the two ranked top-broker lists. */
  overview: (period: BrokerPeriod) => [...brokerKeys.all, 'overview', period] as const,

  /** Overview's "Stocks traded most": one turnover ranking of symbols, not brokers. */
  stocksTradedMost: (period: BrokerPeriod, limit: number) =>
    [...brokerKeys.all, 'stocks-traded-most', period, limit] as const,

  /** All brokers: the sortable, searchable, paged member table. */
  list: (query: BrokerListQuery) => [...brokerKeys.all, 'list', query] as const,

  /** Most traded: stocks that churned most for their size. */
  busyNames: (query: BusyNamesQuery) => [...brokerKeys.all, 'busy-names', query] as const,

  /**
   * Sector flow, and one sector's brokers. Two branches, not `sector-flow` with
   * an optional sector: `SectorFlow` is a list of sectors and `SectorBrokers`
   * is a list of brokers, and drilling in must not be able to answer with the
   * page you drilled from.
   */
  sectorFlow: (period: BrokerPeriod) => [...brokerKeys.all, 'sector-flow', period] as const,
  sectorBrokers: (sector: string, period: BrokerPeriod) =>
    [...brokerKeys.all, 'sector-brokers', sector, period] as const,

  /** Sector flow's "Broker by sector" grid: brokers × sectors, a third shape again. */
  brokersBySector: (period: BrokerPeriod, limit: number) =>
    [...brokerKeys.all, 'by-sector', period, limit] as const,

  /** Large trades: tiles + the paged trade list, and the leaderboard beside it. */
  largeTrades: (query: LargeTradesQuery) => [...brokerKeys.all, 'large-trades', query] as const,
  largeTradeLeaders: (query: LargeTradeLeadersQuery) =>
    [...brokerKeys.all, 'large-trade-leaders', query] as const,

  /** By stock: who bought and who sold this name. */
  byStock: (symbol: string, period: BrokerPeriod, range: BrokerDateRange | null = null) =>
    range === null
      ? ([...brokerKeys.all, 'by-stock', symbol, period] as const)
      : ([...brokerKeys.all, 'by-stock', symbol, 'custom', range.start, range.end] as const),

  /**
   * Large holdings: the biggest net positions as a share of paid-up capital.
   *
   * Its own branch, not a variant of `list` or `stocks`: a `LargeHolding` is a
   * (broker, stock) PAIR — one row carries both a symbol and a broker number —
   * and neither of those row shapes has a symbol and a broker code at once.
   */
  largeHoldings: (query: LargeHoldingsQuery) =>
    [...brokerKeys.all, 'large-holdings', query] as const,

  /**
   * PV range, per symbol. The symbol is a path parameter, so it sits in the key
   * as its own segment ahead of the query, the same way `byStock` and
   * `sectorBrokers` carry theirs — `{ buyer, seller, period }` is what the
   * endpoint actually varies on, and there is no paging to hash.
   */
  pvRange: (symbol: string, query: PVRangeQuery) =>
    [...brokerKeys.all, 'pv-range', symbol, query] as const,

  /**
   * Compare's two sub-views. `brokers` is an ORDERED array and stays one, so
   * ["58","07"] and ["07","58"] are different keys — they are different column
   * orders, and the "best per row" winner is read against those columns.
   */
  compare: (brokers: readonly string[], period: BrokerPeriod) =>
    [...brokerKeys.all, 'compare', brokers, period] as const,
  stocksInCommon: (brokers: readonly string[], period: BrokerPeriod) =>
    [...brokerKeys.all, 'stocks-in-common', brokers, period] as const,

  // ── One broker's page ─────────────────────────────────────────────────────

  /** Scope, not a query. Invalidate this to drop everything about one broker. */
  broker: (code: string) => [...brokerKeys.all, 'broker', code] as const,

  /**
   * By broker's whole payload. ONE key for all three tabs on purpose: Overview,
   * Activity and Specialty are client-side toggles over six blocks of the same
   * response (`services/broker/types.py:122`), so three keys would mean three
   * identical requests and three chances for the tabs to disagree about which
   * session they are describing.
   */
  detailFor: (code: string) => [...brokerKeys.broker(code), 'detail'] as const,
  detail: (code: string, period: BrokerPeriod) => [...brokerKeys.detailFor(code), period] as const,

  /** Every stock it traded — a different row shape from the blocks above, so a different branch. */
  stocks: (code: string, query: BrokerStocksQuery) =>
    [...brokerKeys.broker(code), 'stocks', query] as const,
  /**
   * Every stock the broker traded, walked to exhaustion — its own entry, not a
   * variant of `stocks`, because it is a different shape (a plain array, no
   * pager meta) and a different cost. Keying it under `stocks` would let a
   * one-page table read and a whole-list walk collide on the same entry.
   */
  allStocks: (code: string, query: { period: BrokerPeriod; sort: BrokerStockSort }) =>
    [...brokerKeys.broker(code), 'allStocks', query] as const,

  /** Its raw contracts today. */
  floorsheet: (code: string, query: BrokerTradesQuery) =>
    [...brokerKeys.broker(code), 'floorsheet', query] as const,

  /** The ranked strip above and below it. No parameters beyond the code. */
  peers: (code: string) => [...brokerKeys.broker(code), 'peers'] as const,

  /**
   * One page of reviews. `reviewsFor` is the PREFIX of every page, which is
   * what the write path invalidates — posting a review must refresh the
   * average and the histogram on whichever page the member is looking at, and
   * on the pages they scroll back to.
   */
  reviewsFor: (code: string) => [...brokerKeys.broker(code), 'reviews'] as const,
  reviews: (code: string, query: BrokerReviewsQuery) =>
    [...brokerKeys.reviewsFor(code), query] as const,
}
